/* The eager half of the library's Episodes tab. See `dataset-search.ts` for why a
 * route's search vocabulary lives apart from the screen that reads it.
 *
 * The format enum is imported as a type only: its runtime lives in the generated-RPC chunk,
 * which the router's `validateSearch` must not pull into first paint. The literals below are
 * checked against the enum at build, and `episodes-search.test.ts` holds them total over it. */

import type { EpisodeLabelState, LabelProduct } from "@sx/protocol/experience/catalog/annotations";
import type {
  CatalogEpisodeFormat,
  CatalogEpisodeOrder,
  StockState,
} from "@sx/protocol/experience/catalog/library";

import { integerParam, trimmedStringParam } from "../../../lib/url-state";
import { LABEL_PRODUCTS, LABEL_STATES } from "../labelling";

export interface EpisodesSearch {
  readonly q?: string;
  readonly format?: CatalogEpisodeFormat;
  /** Only the episodes in this place against the shippable rule (Supply's Stock links here). */
  readonly stock?: StockState;
  /** Only the episodes of this Project task (the Tasks page links here). */
  readonly task?: string;
  /** Only the episodes whose labels stand here, so the unlabelled can be selected. */
  readonly labels?: EpisodeLabelState;
  /** With `labels`: the labels of this product. */
  readonly labelsOf?: LabelProduct;
  /** The order the list comes in; absent is newest first. */
  readonly order?: CatalogEpisodeOrder;
  /** Which pods' recordings: absent is production pods only (a test pod's recordings are
   *  never sold, so the Library hides them), `test` only a test pod's, `all` every one. */
  readonly purpose?: EpisodesPurpose;
  /** Only the episodes with a problem, or only those with none; absent is either. */
  readonly problems?: EpisodesProblems;
  /** With `problems=with`: only the episodes with this problem (`no_task`, `blur`, ...). */
  readonly problem?: string;
  /** How the episodes are seen: absent is rows, `frames` a gallery of their pictures by
   *  day, `map` every episode placed by how alike its pictures are. */
  readonly view?: EpisodesView;
}

export const EPISODES_VIEWS = ["frames", "map"] as const;
export type EpisodesView = (typeof EPISODES_VIEWS)[number];

export const EPISODES_PURPOSES = ["test", "all"] as const;
export type EpisodesPurpose = (typeof EPISODES_PURPOSES)[number];

export const EPISODES_PROBLEMS = ["with", "without"] as const;
export type EpisodesProblems = (typeof EPISODES_PROBLEMS)[number];

/** A problem's kind as the Catalog names it: a QC issue, a fault or a stock place. */
const PROBLEM_KIND = /^[a-z][a-z0-9_]*$/;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** MCAP, RRD, LeRobot v3 and simulation — every format but unspecified. */
export const EPISODE_FORMATS: readonly CatalogEpisodeFormat[] = [1, 2, 3, 4];

/** Oldest, longest and shortest first — every order but unspecified and the default, newest. */
export const EPISODE_ORDERS: readonly CatalogEpisodeOrder[] = [2, 3, 4];

/** Every place against the shippable rule, in the rule's order — every state but unspecified. */
export const STOCK_STATES: readonly StockState[] = [1, 2, 12, 3, 9, 10, 4, 5, 6, 8, 11, 7];

/** The episode list's address state: a search of at most 200 characters, one format, one
 * place against the shippable rule, one task, one label state, which pods' recordings and
 * whether they have a problem (and which). */
export function parseEpisodesSearch(search: Record<string, unknown>): EpisodesSearch {
  const q = trimmedStringParam(search.q);
  const format = integerParam(search.format, { min: 1 });
  const stock = integerParam(search.stock, { min: 1 });
  const task = trimmedStringParam(search.task);
  const labels = integerParam(search.labels, { min: 1 });
  const labelsOf = integerParam(search.labelsOf, { min: 1 });
  const order = integerParam(search.order, { min: 1 });
  const kind = trimmedStringParam(search.problem);
  const problem =
    kind !== undefined && kind.length <= 100 && PROBLEM_KIND.test(kind) ? kind : undefined;
  // A problem named on its own asks for the episodes with it.
  const problems = EPISODES_PROBLEMS.find((value) => value === search.problems) ??
    (problem === undefined ? undefined : "with");
  return {
    q: q !== undefined && q.length <= 200 ? q : undefined,
    format: EPISODE_FORMATS.find((value) => value === format),
    stock: STOCK_STATES.find((value) => value === stock),
    task: task !== undefined && UUID.test(task) ? task.toLowerCase() : undefined,
    labels: LABEL_STATES.find((value) => value === labels),
    // A product narrows a label state only, so it is read only beside one.
    labelsOf: LABEL_STATES.some((value) => value === labels)
      ? LABEL_PRODUCTS.find((value) => value === labelsOf)
      : undefined,
    order: EPISODE_ORDERS.find((value) => value === order),
    view: EPISODES_VIEWS.find((value) => value === search.view),
    purpose: EPISODES_PURPOSES.find((value) => value === search.purpose),
    problems,
    // A kind narrows the episodes with problems only, so it is read only beside them.
    problem: problems === "with" ? problem : undefined,
  };
}
